
import React, { useState } from 'react';
import { Send, CheckCircle2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { services } from '../data';

const ContactForm = ({ className }) => {
  const [status, setStatus] = useState('idle'); // idle, loading, success, error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="bg-green-50 border border-green-100 rounded-2xl p-8 text-center space-y-4">
        <div className="flex justify-center">
          <CheckCircle2 size={48} className="text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-[#1b4c2e]">Tak for din henvendelse!</h3>
        <p className="text-green-800">
          Vi har modtaget din besked og vender tilbage til dig hurtigst muligt.
        </p>
        <button 
          onClick={() => setStatus('idle')}
          className="text-green-700 font-semibold hover:underline"
        >
          Send en ny besked
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={cn("space-y-4", className)}>
      {/* Honeypot - Hidden from humans */}
      <div className="sr-only absolute -z-10 opacity-0 pointer-events-none">
        <label htmlFor="website_url">Hvis du er menneske, så lad dette felt være tomt</label>
        <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="name" className="text-sm font-medium text-gray-700">Navn</label>
          <input
            type="text"
            id="name"
            name="name"
            required
            placeholder="Dit fulde navn"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="phone" className="text-sm font-medium text-gray-700">Telefon</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            required
            placeholder="Dit telefonnummer"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
          />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-medium text-gray-700">Email</label>
        <input
          type="email"
          id="email"
          name="email"
          required
          placeholder="din@email.dk"
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="service" className="text-sm font-medium text-gray-700">Hvad kan vi hjælpe med?</label>
        <select
          id="service"
          name="service"
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all appearance-none bg-white"
        >
          <option value="">Vælg en ydelse</option>
          {services.map((service) => (
            <option key={service.id} value={service.slug}>
              {service.title}
            </option>
          ))}
          <option value="andet">Andet</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="date" className="text-sm font-medium text-gray-700">Ønsket dato</label>
          <input
            type="date"
            id="date"
            name="preferred_date"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
          />
        </div>
        <div className="flex items-end pb-3">
          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="checkbox"
              name="flexible_date"
              className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500 transition-all"
            />
            <span className="text-sm font-medium text-gray-700 group-hover:text-green-700 transition-colors">
              Flexibel dato
            </span>
          </label>
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="message" className="text-sm font-medium text-gray-700">Besked</label>
        <textarea
          id="message"
          name="message"
          rows="4"
          required
          placeholder="Beskriv dit projekt..."
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all resize-none"
        ></textarea>
      </div>

      <div className="flex justify-center pt-2">
        <button
          type="submit"
          disabled={status === 'loading'}
          className="w-[289px] sm:w-fit mx-auto bg-green-700 text-white font-bold py-2 sm:py-3 px-6 rounded-xl hover:bg-green-800 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-green-200 disabled:opacity-70 text-sm sm:text-base"
        >
          {status === 'loading' ? (
            <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
          ) : (
            <>
              <Send size={18} />
              Få et uforpligtende tilbud
            </>
          )}
        </button>
      </div>

      {status === 'error' && (
        <p className="text-red-500 text-sm text-center">Der skete en fejl. Prøv venligst igen eller ring til os.</p>
      )}
    </form>
  );
};

export default ContactForm;
